<script setup lang="ts">
// A short list of addresses or host names as chips: type one and press
// Enter (or a comma, or leave the field), × removes one. Each is checked
// as it is added (check returns why it is not valid, '' when it is), the
// count against max, so the list never holds what the node would refuse.
import { computed, ref } from 'vue'

const list = defineModel<string[]>({ required: true })
const props = defineProps<{
    id: string
    placeholder?: string
    max: number
    check: (s: string) => string
}>()
const draft = ref('')
const error = ref('')
const full = computed(() => list.value.length >= props.max)
function add() {
    const v = draft.value.trim().replace(/,$/, '')
    error.value = ''
    if (!v) return
    if (list.value.includes(v)) return void (error.value = `${v} is already listed`)
    if (full.value) return void (error.value = `at most ${props.max}`)
    const why = props.check(v)
    if (why) return void (error.value = `${v}: ${why}`)
    list.value = [...list.value, v]
    draft.value = ''
}
function remove(i: number) {
    list.value = list.value.filter((_, j) => j !== i)
    error.value = ''
}
function key(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault()
        add()
    } else if (e.key === 'Backspace' && !draft.value && list.value.length) remove(list.value.length - 1)
}
</script>

<template>
    <div>
        <div
            class="flex min-h-8 flex-wrap items-center gap-1 rounded-md border border-line-strong bg-bg px-1.5 py-1 focus-within:border-accent"
        >
            <span
                v-for="(v, i) in list"
                :key="v"
                class="flex items-center gap-1 rounded bg-bg-3 py-0.25 pr-0.5 pl-1.5 font-mono text-xs tabular-nums"
                >{{ v
                }}<button
                    type="button"
                    class="h-4 w-4 rounded-sm btn-ghost p-0 text-[11px] leading-none text-fg-3 hover:text-fg"
                    :aria-label="`remove ${v}`"
                    @click="remove(i)"
                >
                    ×
                </button></span
            >
            <input
                v-if="!full"
                :id="id"
                v-model="draft"
                class="m-0 h-6 min-w-24 flex-1 border-0 bg-transparent p-0 px-0.5 text-xs focus:outline-none"
                :placeholder="list.length ? 'add another' : placeholder"
                @keydown="key"
                @blur="add"
            />
        </div>
        <div v-if="error" class="mt-0.5 text-xs text-bad" role="alert">{{ error }}</div>
    </div>
</template>
